import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface PolicyRow {
  readonly backupPolicyId: string;
  readonly policyCode: string;
  readonly policyName: string;
  readonly backupType: string;
  readonly scheduleFrequency: string;
  readonly retentionDays: number;
  readonly encryptionEnabled: boolean;
  readonly status: string;
}

const TYPES = ['', 'FULL', 'INCREMENTAL', 'DIFFERENTIAL', 'SNAPSHOT', 'DATABASE', 'DOCUMENT', 'CONFIGURATION', 'APPLICATION'];
const STATUSES = ['', 'DRAFT', 'ACTIVE', 'PAUSED', 'RETIRED'];

/**
 * M31 — Backup Monitoring Dashboard (§11 / §31). Register of backup policies
 * with their type, schedule, retention window, encryption posture (BR-05:
 * encryption is mandatory) and activation status. Filterable by backup type and
 * policy status.
 */
export function BackupMonitoringPage(): ReactNode {
  const { t: tr } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<PolicyRow[]>([]);
  const [backupType, setBackupType] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (backupType) params.set('backupType', backupType);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: PolicyRow[] }>(`/v1/bcm/backup-policies${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, backupType, status]);

  return (
    <main aria-label="backup-monitoring">
      <h1>{tr('m31.backup.title')}</h1>
      <div className="filters">
        <label>
          {tr('m31.backup.filterType')}
          <select aria-label="filter-backup-type" value={backupType} onChange={(e) => setBackupType(e.target.value)}>
            {TYPES.map((t) => <option key={t || 'all'} value={t}>{t || tr('m31.backup.all')}</option>)}
          </select>
        </label>
        <label>
          {tr('m31.backup.filterStatus')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || tr('m31.backup.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('m31.backup.loading')}</p> : (
        <>
          <p aria-label="policy-count">{tr('m31.backup.count', { count: rows.length })}</p>
          <table aria-label="policy-table">
            <thead><tr><th>{tr('m31.backup.thCode')}</th><th>{tr('m31.backup.thName')}</th><th>{tr('m31.backup.thType')}</th><th>{tr('m31.backup.thSchedule')}</th><th>{tr('m31.backup.thRetention')}</th><th>{tr('m31.backup.thEncryption')}</th><th>{tr('m31.backup.thStatus')}</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.backupPolicyId}>
                  <td>{r.policyCode}</td><td>{r.policyName}</td><td>{r.backupType}</td>
                  <td>{r.scheduleFrequency}</td><td>{r.retentionDays}d</td>
                  <td>{r.encryptionEnabled ? 'ENCRYPTED' : 'PLAINTEXT'}</td>
                  <td>{r.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
